import { useAtomValue } from "jotai";
import { type FunctionComponent, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { searchResultStreamAtom } from "scmp-app/components/search/atoms";
import { SearchInfoTooltip } from "scmp-app/components/search/info-tooltip";
import type { searchFollowTheTimelineArticleSearchResultStreamItem$key } from "scmp-app/queries/__generated__/searchFollowTheTimelineArticleSearchResultStreamItem.graphql";

import { SearchFollowTheTimelineItem } from "./item";
import { SearchFollowTheTimelineItemEntry } from "./item/entry";
import {
  Container,
  Footer,
  HeaderContainer,
  ListContainer,
  NoResult,
  OuterContainer,
  ShowMoreButton,
  StyledCircularProgress,
  StyledHeaderIcon,
  Title,
  TitleContainer,
} from "./styles";

type Props = {
  className?: string;
};

export const SearchFollowTheTimeline: FunctionComponent<Props> = ({ className }) => {
  const { followTheTimeline, isReceivingFollowTheTimeline } = useAtomValue(searchResultStreamAtom);

  const searchResultStreamItems = useMemo(
    () => followTheTimeline.map(item => item.searchResultStream),
    [followTheTimeline],
  );

  const data = useFragment<searchFollowTheTimelineArticleSearchResultStreamItem$key>(
    graphql`
      fragment searchFollowTheTimelineArticleSearchResultStreamItem on ArticleSearchResultStreamItem
      @relay(plural: true) {
        __typename
        ... on ArticleSearchResultStreamItemTimelineSummary {
          id
          order
          summary
          ...itemSearchFollowTheTimelineArticleSearchResultStreamItemTimelineSummary
        }
        ... on ArticleSearchResultStreamItemTimelineItem {
          id
          order
          itemOrder
          content {
            ...entrySearchFollowTheTimelineItemEntryContent
          }
        }
      }
    `,
    searchResultStreamItems,
  );

  const items = useMemo(() => {
    const summaries = data
      .filter(item => item.__typename === "ArticleSearchResultStreamItemTimelineSummary")
      .toSorted((a, b) => a.order - b.order);
    const summaryItems = data.filter(
      item => item.__typename === "ArticleSearchResultStreamItemTimelineItem",
    );
    return summaries.map(summary => ({
      ...summary,
      timelineItems: summaryItems
        .filter(item => item.order === summary.order && notEmpty(item.content))
        .toSorted((a, b) => a.itemOrder - b.itemOrder),
    }));
  }, [data]);

  const isShowLoading = useMemo(
    () => isReceivingFollowTheTimeline === undefined || isReceivingFollowTheTimeline === true,
    [isReceivingFollowTheTimeline],
  );

  const isNoResult = useMemo(
    () => isReceivingFollowTheTimeline === false && items.length === 0,
    [isReceivingFollowTheTimeline, items.length],
  );

  return (
    <OuterContainer className={className}>
      <Container>
        <HeaderContainer>
          <StyledHeaderIcon />
          <TitleContainer>
            <Title>Follow the timeline</Title>
            <SearchInfoTooltip message="Powered by AI, our timeline widget instantly gives you a clear sequence of events and milestones related to your search, offering added context and clarity." />
          </TitleContainer>
        </HeaderContainer>
        {isNoResult ? (
          <NoResult>
            Try refining your search terms to uncover specific timeline angles or read around the
            topic by browsing the articles below.
          </NoResult>
        ) : (
          <ListContainer>
            {items?.map((item, index) => (
              <SearchFollowTheTimelineItem index={index} key={item.id} reference={item}>
                {(expand, setExpand) => {
                  const displayItemsCount = 2;
                  const renderItems = expand
                    ? item.timelineItems
                    : item.timelineItems.slice(0, displayItemsCount);
                  const isShowExpand = !expand && item.timelineItems.length > displayItemsCount;
                  const showMoreCount = item.timelineItems.length - displayItemsCount;
                  return (
                    <>
                      {renderItems.map(
                        (timelineItem, timelineItemIndex) =>
                          timelineItem.content && (
                            <SearchFollowTheTimelineItemEntry
                              categoryName={item.summary}
                              categoryPosition={index + 1}
                              key={timelineItem.id}
                              reference={timelineItem.content}
                              resultPosition={timelineItemIndex + 1}
                              showMoreCount={showMoreCount}
                            />
                          ),
                      )}
                      {isShowExpand && (
                        <ShowMoreButton onClick={() => setExpand(true)}>
                          + Show {showMoreCount} more
                        </ShowMoreButton>
                      )}
                    </>
                  );
                }}
              </SearchFollowTheTimelineItem>
            ))}
          </ListContainer>
        )}
        {isShowLoading && <StyledCircularProgress size={30} />}
        {!isNoResult && (
          <Footer>
            Not quite what you’re looking for? Try refining your search with more specific keywords.
          </Footer>
        )}
      </Container>
    </OuterContainer>
  );
};

SearchFollowTheTimeline.displayName = "SearchFollowTheTimeline";
